/**
 * AutoPartsServices - services stack for Auto Parts & Accessories listings.
 *
 * Three complementary panels:
 *   a. Fitting & Installation - local garages, mobile fitters
 *   b. Delivery & Collection - courier options for heavy / standard parcels
 *   c. Part Financing - pay-in-installments for high-value parts
 *
 * Every card carries a DEMO badge until a real partner replaces the demo entry
 * via the future partner marketplace. The whole stack respects the global
 * `hide_demo_listings` admin kill switch (called from ListingDetail.jsx).
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import {
  Wrench, Truck, CreditCard, Phone, MapPin, Star, Clock, Package,
  ShieldCheck, AlertCircle, CheckCircle2,
} from 'lucide-react';

// -- Demo providers, 1-2 per service type -----------------------------------
const FITTING_DEMOS_UK = [
  {
    id: 'fit-1', name: 'Halfords Mobile Fitting',
    type: 'Mobile Fitter', tagline: 'They come to your driveway',
    rating: 4.7, reviews: 2140, phone: '0800 054 0038',
    coverage: 'UK-wide, 2-hr slot booking',
    tags: ['Brakes', 'Batteries', 'Wipers', 'Bulbs'],
    price_from: 35, eta: 'Same-day / next-day',
  },
  {
    id: 'fit-2', name: 'Kwik Fit Local Garage - Manchester',
    type: 'Local Garage', tagline: 'Drive in, part fitted while you wait',
    rating: 4.5, reviews: 892, phone: '0161 872 4455',
    coverage: 'M50 3AH - Salford Quays',
    tags: ['Clutch', 'Exhausts', 'Suspension', 'ECU'],
    price_from: 85, eta: 'Book online, drop-in',
  },
];

const FITTING_DEMOS_SL = [
  {
    id: 'fit-1-sl', name: 'AutoLanka Mobile Fitters',
    type: 'Mobile Fitter', tagline: 'They come to your driveway in Colombo',
    rating: 4.6, reviews: 812, phone: '077 555 8899',
    coverage: 'Colombo + suburbs, same-day booking',
    tags: ['Brakes', 'Batteries', 'Wipers', 'Bulbs'],
    price_from: 3500, eta: 'Same-day / next-day',
  },
  {
    id: 'fit-2-sl', name: 'Ideal Motors Workshop - Colombo 05',
    type: 'Local Garage', tagline: 'Drive in, part fitted while you wait',
    rating: 4.7, reviews: 1150, phone: '011 250 1121',
    coverage: 'Havelock Rd, Colombo 05',
    tags: ['Clutch', 'Exhausts', 'Suspension', 'ECU'],
    price_from: 7500, eta: 'Book online, drop-in',
  },
];

const DELIVERY_DEMOS_UK = [
  {
    id: 'del-1', name: 'Palletways - Heavy Freight',
    type: 'Heavy Freight',
    tagline: 'For engines, gearboxes, wheelsets (up to 1000 kg)',
    rating: 4.6, reviews: 1420, phone: '0800 001 2345',
    coverage: 'UK mainland, 24-72hr',
    tags: ['Pallet', 'Kerbside', 'Insurance included'],
    price_from: 65, eta: '48-72 hours',
  },
  {
    id: 'del-2', name: 'DPD Local - Standard Parcels',
    type: 'Standard Courier',
    tagline: 'For alternators, bumpers, mirrors (up to 30 kg)',
    rating: 4.8, reviews: 5240, phone: '0121 275 0500',
    coverage: 'UK-wide, next-day',
    tags: ['Next-Day', 'Tracked', 'Predict Delivery'],
    price_from: 8.5, eta: 'Next-day 1-hr slot',
  },
];

const DELIVERY_DEMOS_SL = [
  {
    id: 'del-1-sl', name: 'Domex Cargo - Heavy Parts',
    type: 'Heavy Freight',
    tagline: 'For engines, gearboxes, wheelsets (up to 1000 kg)',
    rating: 4.5, reviews: 620, phone: '011 471 5555',
    coverage: 'Islandwide, 24-72hr',
    tags: ['Pallet', 'Kerbside', 'Insurance included'],
    price_from: 4500, eta: '48-72 hours',
  },
  {
    id: 'del-2-sl', name: 'Pronto Lanka - Standard Parcels',
    type: 'Standard Courier',
    tagline: 'For alternators, bumpers, mirrors (up to 30 kg)',
    rating: 4.6, reviews: 1820, phone: '011 231 8888',
    coverage: 'Islandwide, next-day',
    tags: ['Next-Day', 'Tracked', 'Cash on Delivery'],
    price_from: 550, eta: 'Next-day',
  },
];

const FINANCE_DEMOS_UK = [
  {
    id: 'fin-1', name: 'Klarna - Pay in 3',
    type: 'Interest-Free Instalments',
    tagline: '0% APR over 3 months - no credit-score hit',
    rating: 4.5, reviews: 8420, phone: null,
    coverage: 'UK, 18+, soft credit check',
    tags: ['0% APR', '3 Monthly Payments', 'No Fees'],
    price_from: 0, eta: 'Instant approval',
    website: 'https://www.klarna.com/uk/',
  },
];

const FINANCE_DEMOS_SL = [
  {
    id: 'fin-1-sl', name: 'Koko - Pay in 3',
    type: 'Interest-Free Instalments',
    tagline: '0% interest over 3 months - Sri Lanka\'s buy-now-pay-later',
    rating: 4.4, reviews: 3120, phone: null,
    coverage: 'SL, 18+, NIC required',
    tags: ['0% Interest', '3 Monthly Payments', 'No Fees'],
    price_from: 0, eta: 'Instant approval',
    website: 'https://www.paykoko.com/',
  },
];

// ---------------------------------------------------------------------------
const ProviderCard = ({ provider, icon: Icon, isDemo, testIdPrefix, currencyPrefix = '£' }) => (
  <div
    className="relative border border-gray-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all bg-white"
    data-testid={`${testIdPrefix}-${provider.id}`}
  >
    {isDemo && (
      <div
        className="absolute top-0 right-0 bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-semibold px-2 py-0.5 rounded-bl"
        data-testid={`${testIdPrefix}-demo-${provider.id}`}
      >
        DEMO
      </div>
    )}
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center border bg-blue-50 border-blue-200 text-blue-600">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <h4 className="font-semibold text-gray-900 text-sm">{provider.name}</h4>
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">{provider.type}</Badge>
        </div>
        <p className="text-xs text-gray-500 italic mt-0.5">{provider.tagline}</p>
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
          <span className="flex items-center gap-0.5 text-amber-600">
            <Star className="h-3 w-3 fill-current" />
            {provider.rating.toFixed(1)}
          </span>
          <span>({provider.reviews.toLocaleString()} reviews)</span>
        </div>
        <div className="flex flex-wrap gap-2 mt-1.5 text-xs text-gray-600">
          <span className="flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            {provider.coverage}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {provider.eta}
          </span>
        </div>
        <div className="flex flex-wrap gap-1 mt-2">
          {provider.tags.map((tag) => (
            <span key={tag} className="text-[10px] bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
              {tag}
            </span>
          ))}
        </div>
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <span className="text-xs font-semibold text-emerald-700">
            {provider.price_from > 0 ? `From ${currencyPrefix}${provider.price_from.toLocaleString()}` : 'No booking fee'}
          </span>
          {provider.phone ? (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => window.open(`tel:${provider.phone.replace(/\s/g, '')}`, '_self')}
              data-testid={`${testIdPrefix}-call-${provider.id}`}
            >
              <Phone className="h-3 w-3 mr-1" />
              Call
            </Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs"
              onClick={() => window.open(provider.website, '_blank', 'noopener')}
              data-testid={`${testIdPrefix}-visit-${provider.id}`}
            >
              Visit
            </Button>
          )}
        </div>
      </div>
    </div>
  </div>
);

const AutoPartsServices = ({ listing }) => {
  const isDemo = !!listing?.is_demo;
  const country = (listing?.country || '').toUpperCase();
  const isSL = country === 'LK' || country === 'SL';

  const fittingDemos = isSL ? FITTING_DEMOS_SL : FITTING_DEMOS_UK;
  const deliveryDemos = isSL ? DELIVERY_DEMOS_SL : DELIVERY_DEMOS_UK;
  const financeDemos = isSL ? FINANCE_DEMOS_SL : FINANCE_DEMOS_UK;
  const currencyPrefix = isSL ? 'Rs. ' : '£';
  const financeThreshold = isSL ? 25000 : 200;
  const financeAuthorityLabel = isSL
    ? 'Koko is licensed by the Central Bank of Sri Lanka - your consumer rights are unaffected.'
    : 'Klarna is FCA authorised - your consumer rights are unaffected.';

  // Financing panel only relevant on higher-value parts
  const price = Number(listing?.price || 0);
  const showFinance = price >= financeThreshold;

  return (
    <div className="space-y-6" data-testid="auto-parts-services-stack">
      {isDemo && (
        <Alert className="border-amber-200 bg-amber-50" data-testid="auto-parts-services-demo-notice">
          <AlertCircle className="h-4 w-4 text-amber-600" />
          <AlertDescription className="text-sm text-amber-800">
            <Badge variant="secondary" className="mr-2 bg-amber-100 text-amber-800 border-amber-200">DEMO</Badge>
            Fitting, delivery and financing options below are illustrative for this demo
            listing. Live providers appear on real seller listings.
          </AlertDescription>
        </Alert>
      )}

      {/* a. Fitting & Installation */}
      <Card data-testid="auto-parts-fitting-section">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Wrench className="h-5 w-5 text-orange-600" />
            <CardTitle className="text-lg">Fitting &amp; Installation</CardTitle>
            <Badge variant="outline" className="text-xs">Approved partners</Badge>
            {isDemo && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="fitting-demo-badge">DEMO</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Don't want to fit it yourself? Book a trusted local garage or mobile fitter.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fittingDemos.map((p) => (
              <ProviderCard key={p.id} provider={p} icon={Wrench} isDemo={true} testIdPrefix="fitting-provider" currencyPrefix={currencyPrefix} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* b. Delivery & Collection */}
      <Card data-testid="auto-parts-delivery-section">
        <CardHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <Truck className="h-5 w-5 text-purple-600" />
            <CardTitle className="text-lg">Delivery &amp; Collection</CardTitle>
            <Badge variant="outline" className="text-xs">Courier partners</Badge>
            {isDemo && (
              <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="delivery-demo-badge">DEMO</Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Get this part delivered to your doorstep with insured courier options.
          </p>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {deliveryDemos.map((p) => (
              <ProviderCard key={p.id} provider={p} icon={Package} isDemo={true} testIdPrefix="delivery-provider" currencyPrefix={currencyPrefix} />
            ))}
          </div>
        </CardContent>
      </Card>

      {/* c. Financing (only on parts >= £200) */}
      {showFinance && (
        <Card data-testid="auto-parts-finance-section">
          <CardHeader>
            <div className="flex items-center gap-2 flex-wrap">
              <CreditCard className="h-5 w-5 text-emerald-600" />
              <CardTitle className="text-lg">Split the Cost</CardTitle>
              <Badge variant="outline" className="text-xs">Instalment plans</Badge>
              {isDemo && (
                <Badge variant="secondary" className="text-xs bg-amber-100 text-amber-800 border-amber-200" data-testid="finance-demo-badge">DEMO</Badge>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              Spread payment across 3 months at 0% APR - no credit-score damage.
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {financeDemos.map((p) => (
                <ProviderCard key={p.id} provider={p} icon={CreditCard} isDemo={true} testIdPrefix="finance-provider" currencyPrefix={currencyPrefix} />
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>{financeAuthorityLabel}</span>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AutoPartsServices;
